import { memo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Button from "@mui/material/Button";
import Breadcrumb from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";

import { useConnectionStore } from "stores/ConnectionStore";

import { FOTS } from "utility/typescript";

import ServiceConnectionInstructions from "components/ServiceConnectionInstructions";
import ServiceConnectionStep from "components/ServiceConnectionStep";

// Disable this check so we match the backend regex exactly.
// eslint-disable-next-line no-useless-escape
const TEAMS_WEBHOOK_REGEX =
  /(https:\/\/[a-zA-Z0-9-.]+\.webhook\.office\.com\/webhookb2\/[a-zA-Z0-9-.]+\/IncomingWebhook\/[a-zA-Z0-9-.]+\/[a-zA-Z0-9-.]+)/i;

const TeamsWebhookConnectSetupPage = memo(
  function TeamsWebhookConnectSetupPage() {
    const [, connectionActions] = useConnectionStore();

    const [webhookUrl, setWebhookUrl] = useState("");
    const [inputError, setInputError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [complete, setComplete] = useState(false);
    const [uuid, setUuid] = useState("");

    const connect = () => {
      setLoading(true);
      connectionActions.teamsWebhookConnect({
        webhookUrl,
        onSuccess,
        onError,
      });
    };

    const reset = () => {
      setWebhookUrl("");
      setInputError(false);
    };

    const onSuccess = (data: any) => {
      setLoading(false);
      toast.success("Successfully connected to Microsoft Teams", {
        position: "bottom-center",
      });
      connectionActions.loadConnections({ reload: true });
      setComplete(true);
      setUuid(data.uuid);
    };

    const onError = () => {
      setLoading(false);
      toast.error("Something went wrong connecting to Microsoft Teams", {
        position: "bottom-center",
      });
    };

    return (
      <>
        <Breadcrumbs>
          <Breadcrumb
            underline="none"
            color="inherit"
            component={Link}
            to="/environment/destinations"
          >
            Environment
          </Breadcrumb>
          <Breadcrumb
            underline="none"
            color="inherit"
            component={Link}
            to="/environment/destinations"
          >
            Destinations
          </Breadcrumb>
          <span />
        </Breadcrumbs>

        <ServiceConnectionInstructions
          label="Microsoft Teams Webhook Connection"
          forceCollapse={complete ? new Date() : undefined}
        >
          <Stack spacing={2}>
            <Typography variant="body1" fontWeight="bold">
              How ThreatKey Microsoft Teams Webhooks work:
            </Typography>
            <Typography variant="body1">
              ThreatKey Microsoft Teams Webhooks are a way for the ThreatKey
              platform to communicate with Microsoft Teams in near real-time.
              When an event happens in ThreatKey, a webhook is sent to the
              Microsoft Teams with information about the event.
            </Typography>
          </Stack>
        </ServiceConnectionInstructions>

        {!complete ? (
          <ServiceConnectionStep disabled={loading}>
            <Typography variant="body1">
              Please paste the <strong>Microsoft Teams Webhook URL</strong> that
              you would like to connect to.
            </Typography>
            <TextField
              fullWidth
              label="Microsoft Teams Webhook URL"
              placeholder="https://teams.webhook.office.com/webhookb2/xxxxxxxx-xxxx-Mxxx-Nxxx-xxxxxxxxxxxx/IncomingWebhook/xxxxxxxx-xxxx-Mxxx-Nxxx-xxxxxxxxxxxx"
              variant="outlined"
              sx={{ mt: 1 }}
              value={webhookUrl}
              onChange={(e: FOTS) => {
                const webhook = e?.target?.value.trim();
                const webhookValid = !(
                  webhook.match(TEAMS_WEBHOOK_REGEX) == null
                );

                setInputError(!!webhook && !webhookValid);
                setWebhookUrl(webhook);
              }}
              error={inputError}
              helperText={
                inputError ? "Invalid Microsoft Teams Webhook URL" : ""
              }
              data-private="lipsum"
            />
            <Stack sx={{ mt: 2 }} spacing={2} direction="row">
              <LoadingButton
                variant="contained"
                onClick={connect}
                loading={loading}
                disabled={!webhookUrl || inputError}
              >
                Connect Teams Webhook
              </LoadingButton>
              <Button variant="text" onClick={reset}>
                Clear
              </Button>
            </Stack>
          </ServiceConnectionStep>
        ) : (
          <ServiceConnectionStep disabled={false}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <CheckCircleOutlineRoundedIcon color="success" sx={{ mr: 1 }} />
              <Typography variant="body1">Done!</Typography>
            </Box>
            <Stack sx={{ mt: 2 }} spacing={1} direction="column">
              <Typography variant="body1">
                <Link
                  to={`/environment/destinations/teams_webhook/${uuid}/notifications`}
                >
                  Click here
                </Link>{" "}
                to go to your Microsoft Teams Webhook notification settings.
              </Typography>
            </Stack>
          </ServiceConnectionStep>
        )}
      </>
    );
  }
);

export default TeamsWebhookConnectSetupPage;
